import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { HardDrive, Save, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function StorageSettingsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (profile) {
      if (profile.role !== 'admin') {
        navigate('/dashboard');
        return;
      }
      fetchSettings();
    }
  }, [profile, navigate]);

  const fetchSettings = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .in('setting_key', [
        'storage_provider',
        'storage_bucket_name',
        'storage_region',
        'storage_access_key',
        'storage_secret_key',
        'storage_max_file_size_mb',
        'storage_allowed_types'
      ]);

    if (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile caricare le impostazioni',
        variant: 'destructive',
      });
    } else if (data) {
      const settingsMap: Record<string, string> = {};
      data.forEach((setting: any) => {
        settingsMap[setting.setting_key] = setting.setting_value || '';
      });
      setSettings(settingsMap);
    }
    setLoading(false);
  };

  const handleSettingChange = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);

    try {
      const updates = Object.entries(settings).map(([key, value]) =>
        supabase
          .from('app_settings')
          .update({
            setting_value: value,
            updated_at: new Date().toISOString(),
            updated_by: profile?.id,
          })
          .eq('setting_key', key)
      );

      const results = await Promise.all(updates);
      const hasError = results.some(result => result.error);

      if (hasError) {
        throw new Error('Failed to update some settings');
      }

      toast({
        title: 'Impostazioni salvate',
        description: 'Le configurazioni storage sono state aggiornate',
      });
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile salvare le impostazioni',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard/settings')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <HardDrive className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Configurazione Storage</h1>
              <p className="text-gray-600 mt-1">Gestisci lo spazio web per video e foto</p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Provider Storage</CardTitle>
            <CardDescription>
              Configura il servizio di archiviazione per file multimediali
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="storage_provider">Provider</Label>
              <select
                id="storage_provider"
                value={settings['storage_provider'] || 'supabase'}
                onChange={(e) => handleSettingChange('storage_provider', e.target.value)}
                className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <option value="supabase">Supabase Storage</option>
                <option value="s3">Amazon S3</option>
                <option value="cloudinary">Cloudinary</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="storage_bucket_name">Nome Bucket</Label>
              <Input
                id="storage_bucket_name"
                type="text"
                value={settings['storage_bucket_name'] || ''}
                onChange={(e) => handleSettingChange('storage_bucket_name', e.target.value)}
                placeholder="nome-bucket"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="storage_region">Regione</Label>
              <Input
                id="storage_region"
                type="text"
                value={settings['storage_region'] || ''}
                onChange={(e) => handleSettingChange('storage_region', e.target.value)}
                placeholder="eu-west-1"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="storage_access_key">Access Key</Label>
              <div className="relative">
                <Input
                  id="storage_access_key"
                  type={showSecrets['storage_access_key'] ? 'text' : 'password'}
                  value={settings['storage_access_key'] || ''}
                  onChange={(e) => handleSettingChange('storage_access_key', e.target.value)}
                  placeholder="Access Key ID"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret('storage_access_key')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSecrets['storage_access_key'] ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="storage_secret_key">Secret Key</Label>
              <div className="relative">
                <Input
                  id="storage_secret_key"
                  type={showSecrets['storage_secret_key'] ? 'text' : 'password'}
                  value={settings['storage_secret_key'] || ''}
                  onChange={(e) => handleSettingChange('storage_secret_key', e.target.value)}
                  placeholder="Secret Access Key"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret('storage_secret_key')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSecrets['storage_secret_key'] ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="storage_max_file_size_mb">Dimensione massima file (MB)</Label>
              <Input
                id="storage_max_file_size_mb"
                type="number"
                value={settings['storage_max_file_size_mb'] || ''}
                onChange={(e) => handleSettingChange('storage_max_file_size_mb', e.target.value)}
                placeholder="100"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="storage_allowed_types">Tipi di file consentiti</Label>
              <Textarea
                id="storage_allowed_types"
                value={settings['storage_allowed_types'] || ''}
                onChange={(e) => handleSettingChange('storage_allowed_types', e.target.value)}
                placeholder="image/jpeg,image/png,video/mp4"
                rows={3}
              />
              <p className="text-xs text-gray-500">
                Inserisci i MIME types separati da virgola
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Provider Storage Consigliati</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li><strong>Supabase Storage:</strong> Integrato con il progetto, gratuito fino a 1GB</li>
                  <li><strong>Amazon S3:</strong> Scalabile e affidabile, pay-as-you-go</li>
                  <li><strong>Cloudinary:</strong> Ottimo per immagini e video, con trasformazioni automatiche</li>
                </ul>
                <p className="text-sm text-gray-700 mt-3">
                  <strong>Nota:</strong> Supabase Storage e gia configurato automaticamente. Configura altri provider solo se necessario.
                </p>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="flex justify-end">
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
            size="lg"
            className="min-w-[200px]"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
